import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faFloppyDisk } from '@fortawesome/pro-regular-svg-icons';
import { faCircleStop } from '@fortawesome/pro-solid-svg-icons';
import type { WithT } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign } from '@feathr/blackbox';
import { CampaignClass, CampaignState } from '@feathr/blackbox';
import type { ITooltipProps, TActionButtonType } from '@feathr/components';
import { ButtonValid, Icon } from '@feathr/components';
import { useCampaign, useCampaignText, useMinPackageTooltip } from '@feathr/extender/hooks';
import { useAccount } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';
import type { IBaseAttributes, IValidation, Model } from '@feathr/rachis';

import type { ICampaignValidationErrors } from '../../CampaignSummary';
import { isGoogleAdsSmartDraftPublishBlocked, save } from './SaveCampaignButton.utils';

export interface ISaveProps extends WithT {
  accountId: string;
  campaign: Campaign;
  childModels: Array<Model<IBaseAttributes>>;
  grandchildModels?: Array<Model<IBaseAttributes>>;
  name?: string;
  preSave?: (campaign: Campaign) => Promise<void> | void;
  postSave?: (campaign: Campaign, stateChanged?: boolean) => Promise<void> | void;
  shouldChangeState?: boolean;
  type?: TActionButtonType;
  tooltipPosition?: ITooltipProps['position'];
}

interface ISaveButtonProps extends Omit<ISaveProps, 't' | 'accountId'> {
  disabled?: boolean;
  /** When provided, intercepts publish clicks (Draft/Stopped/Complete → Published) and calls this instead of save(). Stop actions are unaffected. */
  onPublishClick?: () => void;
  /** When provided, intercepts any non-state-changing save click and calls this instead of save(). */
  onSaveClick?: () => void;
  showIcon?: boolean;
  validate?: () => ICampaignValidationErrors | IValidation<ICampaignValidationErrors>;
}

function SaveCampaignButton({
  campaign,
  childModels,
  grandchildModels = [],
  disabled,
  name,
  onPublishClick,
  onSaveClick,
  preSave,
  postSave,
  shouldChangeState = false,
  showIcon = false,
  validate,
  tooltipPosition,
  type = 'secondary',
}: Readonly<ISaveButtonProps>): JSX.Element {
  const account = useAccount();
  const { t } = useTranslation();
  const state = campaign.get('state', CampaignState.Draft);
  const text = useCampaignText();
  const { includedInLicense } = useCampaign({ campaign });
  const minPackageTooltip = useMinPackageTooltip(campaign.get('_cls'));
  const [isSaving, setIsSaving] = useState(false);

  function getLabel(): string {
    if (state === CampaignState.Draft) {
      return shouldChangeState ? t('Publish') : t('Save as draft');
    } else if (
      [
        CampaignState.Scheduled,
        CampaignState.Published,
        CampaignState.Publishing,
        CampaignState.Completing,
        CampaignState.Erroring,
      ].includes(state)
    ) {
      return shouldChangeState ? t('Stop campaign') : t('Save changes');
    }
    return shouldChangeState ? t('Publish') : t('Save changes');
  }

  async function handleSave(): Promise<void> {
    if (!shouldChangeState && onSaveClick) {
      onSaveClick();
      return;
    }

    const isPublishAction =
      shouldChangeState &&
      [CampaignState.Draft, CampaignState.Stopped, CampaignState.Complete].includes(state);

    if (isPublishAction && onPublishClick) {
      onPublishClick();
      return;
    }

    setIsSaving(true);
    try {
      await save({
        campaign,
        childModels,
        grandchildModels,
        preSave,
        postSave,
        shouldChangeState,
        t,
        accountId: account.id,
      });
    } finally {
      setIsSaving(false);
    }
  }

  /*
   * shouldChangeState = true
   *   when draft, stopped, complete => publish
   *   when scheduled, published, or publishing (what about erroring?) => stop campaign
   *
   * shouldChangeState = false
   *   when draft => save draft
   *   when stopped or complete => save changes
   *   when published or publishing (what about erroring?) => save changes
   *
   * Stop button will be disabled for all campaigns past their end date.
   * Save should be disabled: on draft, stopped, or published - when no changes
   * from previous campaign state are present.
   */

  const isPublishingOrCompleting = [CampaignState.Publishing, CampaignState.Completing].includes(
    state,
  );
  const isActiveState =
    [CampaignState.Scheduled, CampaignState.Published, CampaignState.Erroring].includes(state) ||
    isPublishingOrCompleting;

  let buttonTheme = type;
  if (shouldChangeState) {
    buttonTheme = isActiveState ? 'danger' : 'success';
  }

  const childModelsDirty = [...childModels, ...grandchildModels].some(({ isDirty }) => isDirty);
  // If no changes, disable save button.
  const isSaveButtonNoChanges = !shouldChangeState && !campaign.isDirty && !childModelsDirty;
  // If published single send campaign is past start date, disable stop button.
  const isPinpointSingleSendPastStartDate =
    isActiveState &&
    shouldChangeState &&
    campaign.get('_cls') === CampaignClass.PinpointEmail &&
    campaign.isPastStartDate;
  const isStartedMailrailSingleSend =
    campaign.isMailrailSingleSend && !(campaign.isDraft || campaign.isScheduled);
  // If campaign is past end date, disable stop/publish button.
  const isPublishOrStopButtonPastEndDate = shouldChangeState && campaign.isPastEndDate;
  const isErroringMailrail = campaign.isMailrailEmail && campaign.isErroring;
  // Limiting the "no save unless republishing while in stopped state" to Mailrail campaigns for now.
  // The complete state is currently exclusive to Mailrail and requires no extra logic.
  // This restriction avoids unnecessary confusion and support inquiries about what settings the
  // campaign originally ran with.
  const isSaveButtonStoppedOrCompleted =
    !shouldChangeState && (campaign.isComplete || (campaign.isStopped && campaign.isMailrailEmail));
  const publishBlockedByGoogleAdsFlip = isGoogleAdsSmartDraftPublishBlocked(
    campaign,
    shouldChangeState,
  );
  const isReversibleStop =
    campaign.isMailrailTriggerable &&
    shouldChangeState &&
    (campaign.isScheduled || campaign.isPublished);

  // Allow force disabled, else let validation deal with it.
  let isDisabled: boolean | undefined = disabled;
  if (
    isSaveButtonNoChanges ||
    isPublishingOrCompleting ||
    isPublishOrStopButtonPastEndDate ||
    isPinpointSingleSendPastStartDate ||
    isStartedMailrailSingleSend ||
    isErroringMailrail ||
    isSaveButtonStoppedOrCompleted ||
    publishBlockedByGoogleAdsFlip
  ) {
    isDisabled = true;
  }

  // If not already disabled by primary conditions, check license for publish operations only
  if (!isDisabled && shouldChangeState && !campaign.isAnyPartnerMessage && !includedInLicense) {
    isDisabled = true;
  }

  /**
   * Type guard for IValidation
   *
   * When using async validation we need access to additional properties, such
   * as isPending. In that case the validate function passed in should return a
   * IValidation object.
   */
  function isValidation(
    obj?: ICampaignValidationErrors | IValidation<ICampaignValidationErrors>,
  ): obj is IValidation<ICampaignValidationErrors> {
    return !!obj && 'errors' in obj;
  }

  const validation = validate?.();
  const errors = isValidation(validation) ? validation?.errors : validation;
  const isLoading = isValidation(validation) && validation.isPending;

  function getIcon(): IconDefinition {
    if (!shouldChangeState) {
      return faFloppyDisk;
    }
    return isActiveState ? faCircleStop : faCheck;
  }

  // Each of these conditions also forces isDisabled above, so the messages only ever
  // accompany a disabled button.
  function getDisabledReason(): string | undefined {
    if (publishBlockedByGoogleAdsFlip) {
      return text.disableGoogleAdsSmartPublish;
    } else if (isPublishOrStopButtonPastEndDate) {
      return text.disableStopOrRepublishPastEndDate;
    } else if (isStartedMailrailSingleSend) {
      return text.disableSaveStartedSingleSend;
    } else if (isSaveButtonStoppedOrCompleted) {
      return text.disableEditStoppedOrComplete;
    }
    return undefined;
  }

  function getTooltip(): string | undefined {
    if (isDisabled) {
      return getDisabledReason() ?? minPackageTooltip;
    }
    return isReversibleStop ? text.stoppingIsReversible : minPackageTooltip;
  }

  const icon = getIcon();

  return (
    <ButtonValid
      disabled={isDisabled || isSaving || undefined}
      errors={flattenError(errors)}
      isLoading={isLoading || isSaving}
      name={name}
      onClick={handleSave}
      prefix={showIcon ? <Icon icon={icon} /> : undefined}
      tooltip={getTooltip()}
      tooltipPosition={tooltipPosition}
      type={buttonTheme}
    >
      {getLabel()}
    </ButtonValid>
  );
}

export default observer(SaveCampaignButton);
